// Single source of truth for dark-mode color tokens. Keep in sync by hand with
// tokens.css (same names, same values) — theme.js consumes this file for the MUI
// palette, tokens.css exposes the same values as CSS custom properties for
// components/libraries that can't read the MUI theme (e.g. plain CSS, v5 sx props).
export const lightTokens = {
  // background/text values match MUI v4's own createPalette.js light defaults
  // exactly (background.default: grey[50], text.secondary: 0.54 alpha) — this is
  // what createTheme() already produced before this token layer existed, so Phase 1
  // stays a no-op for light mode instead of drifting from MUI's real defaults.
  background: '#fafafa',
  surface: '#ffffff',
  surfaceElevated: '#ffffff',
  textPrimary: 'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(0, 0, 0, 0.54)',
  textDisabled: 'rgba(0, 0, 0, 0.38)',
  border: '#e0e0e0',
  divider: 'rgba(0, 0, 0, 0.12)',
  accentPrimary: '#FF1744',
  accentPrimaryContrast: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.5)',
  // Toolbar/side-nav/drawer chrome (Phase 3, PR-3800). Revised after live design
  // review: the brand pink gradient stays vivid in dark mode (an "ambient glow"
  // direction — near-black neutral chrome, pink reserved for accents/active
  // states) rather than going fully grayscale, which read as washed-out/broken
  // once actually seen on real screens. Same light-mode values either way.
  toolbarBackground: '#ffffff',
  toolbarText: 'rgba(0, 0, 0, 0.87)',
  toolbarTextSecondary: '#555555',
  toolbarBorder: '#707070',
  toolbarHoverBg: '#e3e9f0',
  toolbarAccentStop1: '#FF0076',
  toolbarAccentStop2: '#FF0054',
  toolbarAccentStop3: '#FF4D2A',
  toolbarAccentAlt: '#ff3343',
  toolbarAccentContrast: '#ffffff',
  toolbarAccentShadow: 'rgba(255, 0, 84, 0.25)',
  toolbarSubmenuBg: 'rgb(255 120 120 / 45%)',
  toolbarBase: '#0371AD',
  // Tooltip surface is intentionally the same in both modes (a fixed dark
  // bubble + white text, matching Material Design's own tooltip convention
  // regardless of app theme) — still routed through tokens rather than left
  // as raw literals, so it's included in both lightTokens and darkTokens.
  tooltipSurface: '#333333',
  tooltipSurfaceText: '#ffffff',
  // "Plan"/premium badge gold — a distinct semantic accent from the toolbar's
  // own brand pink, kept unchanged in dark mode (reads fine on a dark surface).
  planAccent: '#FFD23F',
  planAccentLight: '#FFFBD4',
  // Same in both modes — a success-checkmark icon color reads fine regardless of
  // surface darkness, not a themed surface/text decision.
  successIcon: '#73AF55',
  // A distinct red from accentPrimary (#FF1744) — this tooltip variant's own
  // pre-existing color, not the brand primary. Same in both modes; kept as its
  // own token rather than reusing accentPrimary so it doesn't silently drift if
  // the brand primary is retuned for unrelated reasons.
  tooltipPrimaryAccent: '#ff1946',
  // Sidebar active-nav-item highlight. Light mode's sidebar is already the vivid
  // gradient, so the active item never needed its own background — 'transparent'
  // here keeps that exact pre-existing (no background) look byte-identical.
  sidebarActiveBg: 'transparent',
  sidebarActiveText: '#ffffff',
  // Brand blue for links/status text/icons. toolbarBase is a *surface* token (near-black in
  // dark mode), so it must never be used as a foreground color.
  accentBlue: '#0371AD',
  // Outline/accent color of outlined pill buttons and selects. Light keeps the existing orange.
  accentOutline: '#F65026',
  // Semantic brand/status tokens (dark-mode color-usage review). Light values are the brand
  // colors the app already uses; light-mode code paths don't read these, so light is unchanged.
  brandPrimary: '#FF0054',
  brandSecondary: '#FF4D2A',
  brandLime: '#CCFF00',
  brandGradient: 'linear-gradient(90deg, #FF0076 0%, #FF0054 23.8%, #FF4D2A 100%)',
  brandTint: 'rgba(255, 0, 84, 0.08)',
  borderSubtle: '#e0e0e0',
  borderControl: '#707070',
  statusError: '#FF3343',
  chartSeries1: '#FF0076',
  chartSeries2: '#CCFF00',
};

export const darkTokens = {
  // "Ambient glow" direction: a richer near-black (not flat mid-gray) with the
  // brand pink kept vivid throughout, plus a glow shadow token for active/primary
  // elements — the same approach Linear/Raycast/GitHub use for dark mode, chosen
  // after the fully-grayscale first pass read as washed-out on real screens.
  background: '#0f1115',
  surface: '#16181d',
  surfaceElevated: '#1c1f26',
  textPrimary: 'rgba(255, 255, 255, 0.87)',
  textSecondary: 'rgba(255, 255, 255, 0.6)',
  textDisabled: 'rgba(255, 255, 255, 0.38)',
  border: '#262a31',
  divider: 'rgba(255, 255, 255, 0.08)',
  // Stays the vivid brand red: MUI fills contained primary buttons with it under white text, and
  // the lighter on-dark pink would drop that label to 3.2:1. Foreground accents use brandPrimary.
  accentPrimary: '#FF1744',
  accentPrimaryContrast: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.7)',
  toolbarBackground: '#0f1115',
  toolbarText: 'rgba(255, 255, 255, 0.87)',
  toolbarTextSecondary: 'rgba(255, 255, 255, 0.6)',
  toolbarBorder: 'rgba(255, 255, 255, 0.08)',
  toolbarHoverBg: 'rgba(255, 255, 255, 0.06)',
  toolbarAccentStop1: '#FF0076',
  toolbarAccentStop2: '#FF0054',
  toolbarAccentStop3: '#FF4D2A',
  toolbarAccentAlt: '#ff3343',
  toolbarAccentContrast: '#ffffff',
  // A visible glow, not just a soft shadow — used behind active nav items and
  // primary buttons so the accent color reads as "lit" rather than flat.
  toolbarAccentShadow: 'rgba(255, 23, 68, 0.45)',
  toolbarSubmenuBg: 'rgba(255, 255, 255, 0.06)',
  toolbarBase: '#16181d',
  tooltipSurface: '#333333',
  tooltipSurfaceText: '#ffffff',
  planAccent: '#FFD23F',
  planAccentLight: '#FFFBD4',
  successIcon: '#73AF55',
  tooltipPrimaryAccent: '#ff1946',
  // Matches the design mockup's active-nav-item pill exactly: a tinted glow of
  // the brand red rather than a flat white highlight, so the active item reads as
  // "lit" against the near-black sidebar instead of just bold white text.
  sidebarActiveBg: 'rgba(240, 89, 138, 0.14)',
  sidebarActiveText: '#F0598A',
  accentBlue: '#5cb8ec',
  accentOutline: '#F0598A',
  // "On-dark" brand colors: same hue as the brand, 10-20% less saturated and slightly lighter,
  // so branded elements stop vibrating on near-black. Brand pink is reserved for one primary
  // action per screen plus small accents (icons, active state, focus); everything else is neutral.
  brandPrimary: '#F0598A',
  brandSecondary: '#F27A4E',
  brandLime: '#C6E86A',
  // Narrow pink -> coral-red range for filled brand actions (primary CTA, button hover). Deeper
  // than brandPrimary on purpose: white labels on it stay >= 4.5:1 along the whole gradient.
  brandGradient: 'linear-gradient(90deg, #E0165C 0%, #D93A36 100%)',
  brandTint: 'rgba(240, 89, 138, 0.12)',
  borderSubtle: '#2E2E35',
  // Outline of secondary (ghost) buttons — visible on the dark surfaces, still neutral.
  borderControl: 'rgba(255, 255, 255, 0.24)',
  // Errors/critical states only. Kept clearly redder than brandPrimary so branded elements
  // no longer read as alerts.
  statusError: '#FF5A5F',
  // Medium-pastel chart series that sit together without vibrating (no neon lime next to pink).
  chartSeries1: '#F0598A',
  chartSeries2: '#7FB3E6',
};
